import { STRIPE_PAYMENT_ELEMENT_CARD_TYPE } from 'constants/payment'
import {
  INLINE_SPLIT_NEW_CARD_SOURCE,
  INLINE_SPLIT_PRIMARY_CARD_ID,
} from 'checkout/Components/CheckoutSteps/Payment/PurchaseBox/PaymentOptions/SplitPayments/splitCardSource'
import type { InlineSplitCardLeg } from 'checkout/lib/splitPaymentAllocation'
import {
  LOYALTY_EARN_CARD_PAYMENT_TYPE,
  LOYALTY_EARN_CREDIT_PAYMENT_TYPE,
  LOYALTY_EARN_GIFT_CARD_PAYMENT_TYPE,
  LOYALTY_EARN_PAYMENT_TYPES,
  LOYALTY_EARN_POINTS_PAYMENT_TYPE,
  LOYALTY_EARN_VELOCITY_PAYMENT_TYPE,
  LOYALTY_EARN_WALLET_PAYMENT_TYPES,
} from 'loyalty/constants/loyaltyPaymentMethodEarn'
import { nonNullable } from 'lib/array/arrayUtils'
import { getSavedCardPaymentMethodId, isStripePaymentCardV2, type SavedStripeCard } from 'lib/payment/paymentUtils'

interface Params {
  displayedPaymentMethod: App.Checkout.PaymentMethod | null | undefined
  stripePaymentMethod: App.StripePaymentMethod
  /** The cards paying an active split payment, or undefined when the payment isn't split */
  splitPaymentLegs: Array<InlineSplitCardLeg> | undefined
  /** Each newly typed split card's details once its form is complete, by leg id */
  splitNewCardDetailsByLegId: Record<string, App.CheckoutSplitNewCardDetails>
  savedCards: Array<SavedStripeCard>
  creditPaymentAmount: number
  giftCardPaymentAmount: number
  pointsBurnAmount: number
  velocityPointsApplied: number
  /** svc-payment types of the promo, voucher and Société benefit payments the order will include */
  discountPaymentTypes: Array<string>
}

function getCardPaymentMethod({
  walletType,
  funding,
  brand,
}: App.CheckoutSplitNewCardDetails): App.LoyaltyEarnPaymentMethod | undefined {
  if (walletType) {
    return { type: LOYALTY_EARN_WALLET_PAYMENT_TYPES[walletType] ?? walletType }
  }

  // Card details are only known once a saved card is picked or a new card's details are complete
  if (!funding && !brand) return undefined

  return {
    type: LOYALTY_EARN_CARD_PAYMENT_TYPE,
    details: { funding, brand },
  }
}

function getSelectedCardPaymentMethod(
  stripePaymentMethod: App.StripePaymentMethod,
): App.LoyaltyEarnPaymentMethod | undefined {
  return getCardPaymentMethod({
    walletType: stripePaymentMethod.walletType,
    funding: stripePaymentMethod.cardFunding,
    brand: stripePaymentMethod.cardBrand,
  })
}

function getSelectedPaymentMethod(
  displayedPaymentMethod: App.Checkout.PaymentMethod | null | undefined,
  stripePaymentMethod: App.StripePaymentMethod,
): App.LoyaltyEarnPaymentMethod | undefined {
  if (!displayedPaymentMethod) return undefined

  if (displayedPaymentMethod === STRIPE_PAYMENT_ELEMENT_CARD_TYPE) {
    return getSelectedCardPaymentMethod(stripePaymentMethod)
  }

  return { type: LOYALTY_EARN_PAYMENT_TYPES[displayedPaymentMethod] ?? displayedPaymentMethod }
}

function getSavedCardPaymentMethod(savedCard: SavedStripeCard): App.LoyaltyEarnPaymentMethod | undefined {
  // Older saved cards don't record their funding, so they can't be classified
  if (!isStripePaymentCardV2(savedCard)) return undefined

  return {
    type: LOYALTY_EARN_CARD_PAYMENT_TYPE,
    details: { funding: savedCard.funding, brand: savedCard.brand },
  }
}

function getSplitLegPaymentMethod(
  leg: InlineSplitCardLeg,
  { stripePaymentMethod, splitNewCardDetailsByLegId, savedCards }: Params,
): App.LoyaltyEarnPaymentMethod | undefined {
  if (leg.source === INLINE_SPLIT_NEW_CARD_SOURCE) {
    const newCardDetails = splitNewCardDetailsByLegId[leg.id]
    if (newCardDetails) return getCardPaymentMethod(newCardDetails)

    // The first card is typed into the main card form, which also records its details
    return leg.id === INLINE_SPLIT_PRIMARY_CARD_ID ? getSelectedCardPaymentMethod(stripePaymentMethod) : undefined
  }

  const savedCard = savedCards.find(card => getSavedCardPaymentMethodId(card) === leg.source)
  return savedCard ? getSavedCardPaymentMethod(savedCard) : undefined
}

/**
 * Every card in a split payment, or nothing until all of them are known. A partial list could quote
 * the bonus while an unknown card turns out to earn the base rate.
 */
function getSplitPaymentMethods(
  splitPaymentLegs: Array<InlineSplitCardLeg>,
  params: Params,
): Array<App.LoyaltyEarnPaymentMethod> | undefined {
  const legPaymentMethods = splitPaymentLegs.map(leg => getSplitLegPaymentMethod(leg, params))
  const knownPaymentMethods = nonNullable(legPaymentMethods)

  return knownPaymentMethods.length === legPaymentMethods.length ? knownPaymentMethods : undefined
}

function getChosenPaymentMethods(params: Params): Array<App.LoyaltyEarnPaymentMethod | undefined> {
  const { displayedPaymentMethod, stripePaymentMethod, splitPaymentLegs } = params

  // Split payments are only made by card. Choosing another method turns split off just after.
  if (!splitPaymentLegs || displayedPaymentMethod !== STRIPE_PAYMENT_ELEMENT_CARD_TYPE) {
    return [getSelectedPaymentMethod(displayedPaymentMethod, stripePaymentMethod)]
  }

  return getSplitPaymentMethods(splitPaymentLegs, params) ?? []
}

/**
 * Lists every payment the customer has chosen so svc-lux-loyalty can decide the payment-method earn.
 * A split payment sends each of its cards. Credit, gift card, points, Velocity, promo codes, vouchers
 * and Société benefits are sent alongside the selected method or cards because svc-lux-loyalty sees
 * them as payments too. Returns undefined until the rate is known.
 */
function getLoyaltyEarnPaymentMethods(params: Params): Array<App.LoyaltyEarnPaymentMethod> | undefined {
  const {
    creditPaymentAmount,
    giftCardPaymentAmount,
    pointsBurnAmount,
    velocityPointsApplied,
    discountPaymentTypes,
  } = params
  const chosenPaymentMethods = nonNullable(getChosenPaymentMethods(params))

  // Gift card, points, promo, voucher and benefit payments don't change the rate, so it's only known
  // once a method is chosen or credit or Velocity pays (both earn the base rate)
  if (!chosenPaymentMethods.length && creditPaymentAmount <= 0 && velocityPointsApplied <= 0) return undefined

  return nonNullable([
    ...chosenPaymentMethods,
    creditPaymentAmount > 0 ? { type: LOYALTY_EARN_CREDIT_PAYMENT_TYPE } : undefined,
    giftCardPaymentAmount > 0 ? { type: LOYALTY_EARN_GIFT_CARD_PAYMENT_TYPE } : undefined,
    pointsBurnAmount > 0 ? { type: LOYALTY_EARN_POINTS_PAYMENT_TYPE } : undefined,
    velocityPointsApplied > 0 ? { type: LOYALTY_EARN_VELOCITY_PAYMENT_TYPE } : undefined,
    ...discountPaymentTypes.map(type => ({ type })),
  ])
}

export default getLoyaltyEarnPaymentMethods
